import React, { useMemo } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import { createBrowserRouter, Navigate, RouteObject } from "react-router";
import { RouterProvider } from "react-router/dom";
import { useShallow } from "zustand/react/shallow";

import { ActivityType, TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";
import { AppLayout } from "app/components/AppLayout";
import { NotUsingPWAPage } from "app/pages/NotUsingPWA";
import { AppLayoutProvider, RouteLayoutProps, Flag } from "app/providers/AppLayoutProvider";
import { useAppPWAStateStore } from "app/stores/useAppPWAStateStore";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { EppoFeatureFlags } from "app/types/flags";
import { config } from "config";
import { routes as routesConfig } from "config/routes";
import { ActivityRouteTracker } from "flows/Activities/components/ActivityRouteTracker";
import { RestockingActivityTracker } from "flows/Activities/components/RestockingActivityTracker";
import { StockCorrectionActivityTracker } from "flows/Activities/components/StockCorrectionActivityTracker";
import { ActivitiesIndexPage } from "flows/Activities/pages/ActivitiesIndex";
import { DeliveryCheckIn } from "flows/Activities/pages/DeliveryCheckIn/DeliveryCheckIn";
import { FeatureFlagConfig } from "flows/Activities/pages/FeatureFlagConfig";
import { TimedActivitiesDetails } from "flows/Activities/pages/TimedActivitiesDetails/TimedActivitiesDetails";
import { TimedActivitiesListPage } from "flows/Activities/pages/TimedActivitiesList";
import { TransportEquipmentConfirmationPage } from "flows/Activities/pages/TransportEquipment/TransportEquipmentConfirmation";
import { TransportEquipmentCountPage } from "flows/Activities/pages/TransportEquipment/TransportEquipmentCount";
import { TransportEquipmentPrecountPage } from "flows/Activities/pages/TransportEquipment/TransportEquipmentPrecount";
import { AuthPage } from "flows/Auth/pages/Auth";
import { BatteryCountListPage } from "flows/BatteryManagement/pages/BatteryCountListPage";
import { BatteryCountMissingPage } from "flows/BatteryManagement/pages/BatteryCountMissingPage";
import { BatteryCountScanPage } from "flows/BatteryManagement/pages/BatteryCountScanPage";
import { BatteryCreatePage } from "flows/BatteryManagement/pages/BatteryCreatePage";
import { BatteryManagementListPage } from "flows/BatteryManagement/pages/BatteryManagementListPage";
import { BatteryReportPage } from "flows/BatteryManagement/pages/BatteryReportPage";
import { CollaborativeInbound } from "flows/Inbound/pages/CollaborativeInbound/CollaborativeInbound";
import { InboundDroppingListPage } from "flows/Inbound/pages/InboundDroppingListPage/InboundDroppingListPage";
import { InboundEditQuantityPage } from "flows/Inbound/pages/InboundEditQuantityPage";
import { OutboundClaimedProduct } from "flows/Inbound/pages/InboundOutboundPage/OutboundClaimedProduct";
import { OutboundUnclaimedProduct } from "flows/Inbound/pages/InboundOutboundPage/OutboundUnclaimedProduct";
import { InboundPreviewProductsPage } from "flows/Inbound/pages/InboundPreviewProductsPage/InboundPreviewProductsPage";
import { InboundSharedList } from "flows/Inbound/pages/InboundSharedList/InboundSharedList";
import { InboundSharedListVerification } from "flows/Inbound/pages/InboundSharedListVerification/InboundSharedListVerification";
import { ManualProductsSearchPage } from "flows/Inbound/pages/ManualProductsSearchPage/ManualProductsSearchPage";
import { PreDroppingListPage } from "flows/Inbound/pages/PreDroppingListPage/PreDroppingListPage";
import { ProductsSearchNoEANProductsListPage } from "flows/Inbound/pages/ProductsSearchNoEANProductsListPage/ProductsSearchNoEANProductsListPage";
import { ScanRolliPage } from "flows/Inbound/pages/ScanRolliPage/ScanRolliPage";
import { SelectDeliveryPage } from "flows/Inbound/pages/SelectDeliveryPage/SelectDeliveryPage";
import { BbdCheckDeclareClosestBdd } from "flows/Inventory/flows/BbdChecks/pages/BbdCheckDeclareClosestBdd/BbdCheckDeclareClosestBdd";
import { BbdCheckDeclareExpired } from "flows/Inventory/flows/BbdChecks/pages/BbdCheckDeclareExpired/BbdCheckDeclareExpired";
import { BbdChecksList } from "flows/Inventory/flows/BbdChecks/pages/BbdChecksList";
import { DeclareBBDCorrection } from "flows/Inventory/flows/BbdCorrections/pages/DeclareBBDCorrection/DeclareBBDCorrection";
import { BbdCorrectionIdlePage } from "flows/Inventory/flows/BbdCorrections/pages/Idle";
import { SelectBbdProductPage } from "flows/Inventory/flows/BbdCorrections/pages/SelectProduct";
import { EoyCheck } from "flows/Inventory/flows/EoyChecks/pages/EoyCheck";
import { EoyChecksList } from "flows/Inventory/flows/EoyChecks/pages/EoyChecksList";
import { RecountChecksList } from "flows/Inventory/flows/EoyChecks/pages/RecountChecksList";
import { FreshChecksList } from "flows/Inventory/flows/FreshChecks/pages/FreshChecksList";
import { FreshCheckTask } from "flows/Inventory/flows/FreshChecks/pages/FreshCheckTask";
import { InboundDeviationCheckListPage } from "flows/Inventory/flows/InboundDeviationChecks/pages/InboundDeviationCheckList";
import { InboundDeviationCheckQuantityPage } from "flows/Inventory/flows/InboundDeviationChecks/pages/InboundDeviationCheckQuantity";
import { RestockingDropOff } from "flows/Inventory/flows/RestockingList/pages/RestockingDropOff/RestockingDropOff";
import { RestockingListPage } from "flows/Inventory/flows/RestockingList/pages/RestockingListPage/RestockingListPage";
import { RestockingListPreparation } from "flows/Inventory/flows/RestockingList/pages/RestockingListPreparation/RestockingListPreparation";
import { RestockingMoveItems } from "flows/Inventory/flows/RestockingList/pages/RestockingMoveItems/RestockingMoveItems";
import { RestockingProductSearch } from "flows/Inventory/flows/RestockingList/pages/RestockingProductSearch/RestockingProductSearch";
import { RestockingPublicList } from "flows/Inventory/flows/RestockingList/pages/RestockingPublicList/RestockingPublicList";
import { RollingInventoryCheckQuantity } from "flows/Inventory/flows/RollingInventoryChecks/pages/RollingInventoryCheckQuantity";
import { RollingInventoryChecksList } from "flows/Inventory/flows/RollingInventoryChecks/pages/RollingInventoryChecksList";
import { RollingInventoryCheckUpdate } from "flows/Inventory/flows/RollingInventoryChecks/pages/RollingInventoryCheckUpdate";
import { StockCheckQuantityPage } from "flows/Inventory/flows/StockChecks/pages/StockCheckQuantity";
import { StockChecksList } from "flows/Inventory/flows/StockChecks/pages/StockChecksList";
import { StockCheckUpdatePage } from "flows/Inventory/flows/StockChecks/pages/StockCheckUpdate";
import { DeclareStockCorrectionPage } from "flows/Inventory/flows/StockCorrections/pages/DeclareStockCorrection";
import { IdlePage } from "flows/Inventory/flows/StockCorrections/pages/Idle";
import { SelectProductPage } from "flows/Inventory/flows/StockCorrections/pages/SelectProduct";
import { TGTGBagConfirmationPage } from "flows/Inventory/flows/TooGoodToGo/pages/TGTGBagConfirmationPage";
import { TGTGBagSelectionPage } from "flows/Inventory/flows/TooGoodToGo/pages/TGTGBagSelectionPage";
import { TGTGPrepareBagPage } from "flows/Inventory/flows/TooGoodToGo/pages/TGTGPrepareBagPage";
import { TGTGSelectNoEANProductPage } from "flows/Inventory/flows/TooGoodToGo/pages/TGTGSelectNoEANProductPage";
import { TGTGSelectProductPage } from "flows/Inventory/flows/TooGoodToGo/pages/TGTGSelectProductPage";
import { InventoryPage } from "flows/Inventory/shared/pages/Inventory";
import { AssignShelvesPage } from "flows/Picking/pages/AssignShelves/AssignShelves";
import { ManualPickingPage } from "flows/Picking/pages/ManualPicking";
import { PickingDonePage } from "flows/Picking/pages/PickingDone/PickingDone";
import { PickingIncomplete } from "flows/Picking/pages/PickingIncomplete/PickingIncomplete";
import { PickingIndexPage } from "flows/Picking/pages/PickingIndex/PickingIndex";
import { PickingListPage } from "flows/Picking/pages/PickingList/PickingList";
import { ScanContainersPage } from "flows/Picking/pages/ScanContainers/ScanContainers";
import { ShelfChangeDetailsPage } from "flows/ShelfChanges/pages/ShelfChangeDetailsPage";
import { ShelfChangesPage } from "flows/ShelfChanges/pages/ShelfChangesPage";

// Routes allowed when EOY mode is active
const ALLOWED_IN_EOY_MODE_FLAG = {
  featureFlag: EppoFeatureFlags.EOY_SHOW_CHECKS,
  inaccessible: false,
  redirectPath: routesConfig.inventory.root,
};

// Routes blocked when EOY mode is active
const BLOCKED_IN_EOY_MODE_FLAG = {
  featureFlag: EppoFeatureFlags.EOY_MODE_ENABLED,
  inaccessible: true,
  redirectPath: routesConfig.inventory.root,
};

// Routes blocked due to restricted corrections during EOY
const BLOCKED_CORRECTIONS_IN_EOY_MODE_FLAG = {
  featureFlag: EppoFeatureFlags.EOY_HIDE_CORRECTIONS,
  inaccessible: true,
  redirectPath: routesConfig.inventory.root,
};

// Enable Shelf Change Assistant Flow
const ENABLE_SHELF_CHANGE_ASSISTANT_FLAG = {
  featureFlag: EppoFeatureFlags.ENABLE_SHELF_CHANGE_ASSISTANT,
  inaccessible: false,
  redirectPath: routesConfig.inventory.root,
};

// Enable Transport Equipment Flow
const ENABLE_TRANSPORT_EQUIPMENT_FLOW_FLAG = {
  featureFlag: EppoFeatureFlags.ENABLE_TRANSPORT_EQUIPMENT_FLOW,
  inaccessible: false,
  redirectPath: routesConfig.activities.root,
};

export type CustomRouteObject = {
  caseSensitive?: boolean;
  children?: CustomRouteObject[];
  element?: React.ReactNode;
  path?: string;
  id?: string;
  flag?: Flag;
  noLayout?: boolean;
} & RouteLayoutProps;

const generateRoutes = (routes: CustomRouteObject[]): RouteObject[] =>
  routes.map(
    ({
      element,
      path,
      id,
      noLayout,
      withTabBar,
      highlightedTab,
      canGoBack,
      withWaitingOrdersBannerOfType,
      withMenu,
      withOfflineBanner,
      withAppHeader,
      withScrollableAppHeader,
      withOngoingActivityBanner,
      withTabList,
      canShowUpdateModal,
      pageName,
      flag,
      children,
      ...rest
    }) => ({
      ...rest,
      path,
      element: !element ? undefined : noLayout ? (
        element
      ) : (
        <AppLayoutProvider
          id={path || id || ""}
          canGoBack={canGoBack}
          withTabBar={withTabBar}
          highlightedTab={highlightedTab}
          withMenu={withMenu}
          withWaitingOrdersBannerOfType={withWaitingOrdersBannerOfType}
          withOfflineBanner={withOfflineBanner}
          withAppHeader={withAppHeader}
          withScrollableAppHeader={withScrollableAppHeader}
          withOngoingActivityBanner={withOngoingActivityBanner}
          withTabList={withTabList}
          canShowUpdateModal={canShowUpdateModal}
          pageName={pageName}
          flag={flag}
        >
          {element}
        </AppLayoutProvider>
      ),
      children: children ? generateRoutes(children) : undefined,
    }),
  );

const featureFlagConfigRoutes: CustomRouteObject[] =
  config.environment.ENVIRONMENT === "production"
    ? []
    : [
        {
          path: routesConfig.activities.featureFlagConfig,
          canGoBack: true,
          element: <FeatureFlagConfig />,
          withAppHeader: false,
        },
      ];

const isInBrowserRoutes: RouteObject[] = generateRoutes([
  {
    path: "*",
    canGoBack: false,
    withWaitingOrdersBannerOfType: "none",
    withMenu: false,
    withAppHeader: false,
    element: <NotUsingPWAPage />,
  },
]);

const publicRoutes: RouteObject[] = generateRoutes([
  ...featureFlagConfigRoutes,
  {
    path: "*",
    canGoBack: false,
    withWaitingOrdersBannerOfType: "disabled-unidentified",
    element: <AuthPage />,
  },
]);

const protectedRoutes: RouteObject[] = generateRoutes([
  ...featureFlagConfigRoutes,
  {
    path: routesConfig.activities.root,
    canGoBack: false,
    element: <ActivitiesIndexPage />,
    withTabBar: true,
    highlightedTab: "activities",
    withOngoingActivityBanner: true,
    canShowUpdateModal: true,
    pageName: PageName.ACTIVITIES_PAGE,
  },
  {
    path: routesConfig.inbound.root,
    element: <CollaborativeInbound />,
    canGoBack: false,
    withTabBar: true,
    withAppHeader: false,
    highlightedTab: "inbound",
    withOngoingActivityBanner: true,
    withTabList: true,
    pageName: PageName.INBOUND_PAGE_SHARED_LIST,
    flag: BLOCKED_IN_EOY_MODE_FLAG,
    children: [
      {
        path: routesConfig.inbound.root,
        element: <InboundSharedList />,
        canGoBack: false,
        withTabBar: true,
        highlightedTab: "inbound",
        withAppHeader: false,
        withOngoingActivityBanner: true,
        withTabList: true,
        pageName: PageName.INBOUND_PAGE_SHARED_LIST,
      },
      {
        path: routesConfig.inbound.selectDelivery,
        element: <SelectDeliveryPage />,
        canGoBack: false,
        withTabBar: true,
        withAppHeader: false,
        highlightedTab: "inbound",
        withOngoingActivityBanner: true,
        pageName: PageName.INBOUND_SELECT_DELIVERY,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.scanRolli,
        canGoBack: false,
        withTabBar: false,
        withAppHeader: false,
        element: <ScanRolliPage />,
        withOngoingActivityBanner: true,
        highlightedTab: "inbound",
        pageName: PageName.INBOUND_SELECT_DELIVERY,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.preDropping,
        element: <PreDroppingListPage />,
        canGoBack: false,
        withTabBar: true,
        withAppHeader: false,
        highlightedTab: "inbound",
        withOngoingActivityBanner: true,
        pageName: PageName.INBOUND_PAGE,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.previewProducts,
        element: <InboundPreviewProductsPage />,
        canGoBack: false,
        withTabBar: true,
        withAppHeader: false,
        highlightedTab: "inbound",
        withOngoingActivityBanner: true,
        pageName: PageName.INBOUND_PAGE,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.manualSearch,
        withAppHeader: false,
        withTabBar: false,
        element: <ManualProductsSearchPage />,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.noEANProducts,
        withAppHeader: false,
        withTabBar: false,
        element: <ProductsSearchNoEANProductsListPage />,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.outboundUnclaimed,
        withAppHeader: false,
        withTabBar: false,
        element: <OutboundUnclaimedProduct />,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.outboundClaimed,
        withAppHeader: false,
        withTabBar: false,
        element: <OutboundClaimedProduct />,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.editQuantity,
        withAppHeader: false,
        withTabBar: false,
        element: <InboundEditQuantityPage />,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.listVerification,
        withAppHeader: false,
        withTabBar: false,
        element: <InboundSharedListVerification />,
        pageName: PageName.INBOUND_PAGE_LIST_VERIFICATION,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.dropping,
        withAppHeader: false,
        element: <InboundDroppingListPage />,
        pageName: PageName.INBOUND_DROPPING_PAGE,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inbound.droppingManualSearch,
        withAppHeader: false,
        withTabBar: false,
        element: <ManualProductsSearchPage />,
      },
      {
        path: routesConfig.inbound.droppingNoEANProducts,
        withAppHeader: false,
        withTabBar: false,
        element: <ProductsSearchNoEANProductsListPage />,
      },
    ],
  },
  {
    path: routesConfig.inventory.root,
    canGoBack: false,
    element: <InventoryPage />,
    withTabBar: true,
    highlightedTab: "inventory",
    withOngoingActivityBanner: true,
    pageName: PageName.INVENTORY_PAGE,
  },
  // picking
  {
    path: routesConfig.pickingV2.root,
    children: [
      {
        path: routesConfig.pickingV2.root,
        canGoBack: false,
        element: <PickingIndexPage />,
        withTabBar: true,
        highlightedTab: "picking",
        withWaitingOrdersBannerOfType: "none",
        withOngoingActivityBanner: true,
        pageName: PageName.NEW_ORDER_FOR_PICKING_PAGE,
      },
      {
        noLayout: true,
        element: <ActivityRouteTracker activityType={ActivityType.picking} />,
        children: [
          {
            path: routesConfig.pickingV2.incomplete,
            element: <PickingIncomplete />,
            canGoBack: false,
            withOngoingActivityBanner: false,
            withWaitingOrdersBannerOfType: "none",
          },
          {
            path: routesConfig.pickingV2.start,
            canGoBack: false,
            element: <PickingListPage />,
            withTabBar: false,
            withAppHeader: false,
            withWaitingOrdersBannerOfType: "none",
            withMenu: false,
            pageName: PageName.PICKING_PAGE,
          },
          {
            path: routesConfig.pickingV2.containers,
            element: <ScanContainersPage />,
            withTabBar: false,
            withMenu: false,
            withWaitingOrdersBannerOfType: "none",
          },
          {
            path: routesConfig.pickingV2.shelves,
            element: <AssignShelvesPage />,
            withTabBar: false,
            withMenu: false,
            withWaitingOrdersBannerOfType: "none",
          },
          {
            path: routesConfig.pickingV2.end,
            canGoBack: false,
            element: <PickingDonePage />,
            withWaitingOrdersBannerOfType: "none",
            withMenu: false,
          },
          {
            path: routesConfig.pickingV2.manual,
            canGoBack: true,
            element: <ManualPickingPage />,
            withWaitingOrdersBannerOfType: "none",
            withOngoingActivityBanner: true,
            pageName: PageName.MANUAL_PICKING_PAGE,
          },
        ],
      },
    ],
  },
  {
    path: routesConfig.inventory.stockCorrections.root,
    flag: BLOCKED_IN_EOY_MODE_FLAG,
    noLayout: true,
    element: <StockCorrectionActivityTracker />,
    children: [
      {
        path: routesConfig.inventory.stockCorrections.root,
        canGoBack: true,
        element: <IdlePage />,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.stockCorrections.declare,
        canGoBack: true,
        element: <DeclareStockCorrectionPage />,
        withTabBar: false,
        withAppHeader: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.stockCorrections.selectProduct,
        canGoBack: true,
        element: <SelectProductPage />,
        withTabBar: false,
        withAppHeader: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
    ],
  },
  {
    path: routesConfig.inventory.restockingList.root,
    element: (
      <RestockingActivityTracker>
        <RestockingListPage />
      </RestockingActivityTracker>
    ),
    canGoBack: true,
    withTabBar: true,
    withAppHeader: false,
    highlightedTab: "inventory",
    withOngoingActivityBanner: true,
    withTabList: true,
    flag: BLOCKED_IN_EOY_MODE_FLAG,
    pageName: PageName.INVENTORY_PAGE_PRIVATE_LIST,
    children: [
      {
        path: routesConfig.inventory.restockingList.root,
        element: <RestockingListPreparation />,
        canGoBack: true,
        withTabBar: true,
        highlightedTab: "inventory",
        withAppHeader: false,
        withOngoingActivityBanner: true,
        withTabList: true,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
        pageName: PageName.INVENTORY_PAGE_PRIVATE_LIST,
      },
      {
        path: routesConfig.inventory.restockingList.publicList,
        element: <RestockingPublicList />,
        canGoBack: true,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        withTabList: true,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
        pageName: PageName.INVENTORY_PAGE_PUBLIC_LIST,
      },
      {
        path: routesConfig.inventory.restockingList.moveItems,
        element: <RestockingMoveItems />,
        canGoBack: true,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
        withTabList: true,
        pageName: PageName.INVENTORY_PAGE_MOVE_ITEMS,
      },
      {
        path: routesConfig.inventory.restockingList.dropOff,
        element: <RestockingDropOff />,
        canGoBack: true,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        pageName: PageName.INVENTORY_PAGE_DROP_OFF,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.restockingList.shelfSearch,
        element: <RestockingProductSearch mode="shelf" />,
        canGoBack: false,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.restockingList.textSearch,
        element: <RestockingProductSearch mode="text" />,
        canGoBack: false,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
    ],
  },
  {
    path: routesConfig.inventory.bbdCorrections.root,
    flag: BLOCKED_IN_EOY_MODE_FLAG,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.inventory_checks} />,
    children: [
      {
        path: routesConfig.inventory.bbdCorrections.root,
        canGoBack: true,
        element: <BbdCorrectionIdlePage />,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.bbdCorrections.declare,
        canGoBack: true,
        element: <DeclareBBDCorrection />,
        withTabBar: false,
        withAppHeader: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.bbdCorrections.selectProduct,
        canGoBack: true,
        element: <SelectBbdProductPage />,
        withTabBar: false,
        withAppHeader: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
    ],
  },
  {
    path: routesConfig.inventory.stockChecks.root,
    pageName: PageName.INVENTORY_STOCK_CHECK_LIST_PAGE,
    flag: BLOCKED_CORRECTIONS_IN_EOY_MODE_FLAG,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.inventory_checks} />,
    children: [
      {
        path: routesConfig.inventory.stockChecks.root,
        element: <StockChecksList />,
        canGoBack: true,
        withMenu: false,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
      },
      {
        path: routesConfig.inventory.stockChecks.quantity,
        canGoBack: true,
        element: <StockCheckQuantityPage />,
        withMenu: false,
        withAppHeader: false,
      },
      {
        path: routesConfig.inventory.stockChecks.update,
        canGoBack: true,
        element: <StockCheckUpdatePage />,
        withMenu: false,
        withAppHeader: false,
      },
    ],
  },
  {
    path: routesConfig.inventory.rollingInventoryChecks.root,
    pageName: PageName.INVENTORY_ROLLING_INVENTORY_CHECK_LIST_PAGE,
    flag: BLOCKED_CORRECTIONS_IN_EOY_MODE_FLAG,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.inventory_checks} />,
    children: [
      {
        path: routesConfig.inventory.rollingInventoryChecks.root,
        element: <RollingInventoryChecksList />,
        canGoBack: true,
        withMenu: false,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
      },
      {
        path: routesConfig.inventory.rollingInventoryChecks.quantity,
        canGoBack: true,
        element: <RollingInventoryCheckQuantity />,
        withMenu: false,
        withAppHeader: false,
      },
      {
        path: routesConfig.inventory.rollingInventoryChecks.update,
        canGoBack: true,
        element: <RollingInventoryCheckUpdate />,
        withMenu: false,
        withAppHeader: false,
      },
    ],
  },
  {
    path: routesConfig.inventory.eoyChecks.root,
    pageName: PageName.INVENTORY_EOY_CHECK_LIST_PAGE,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.inventory_checks} />,
    children: [
      {
        path: routesConfig.inventory.eoyChecks.root,
        canGoBack: true,
        element: <EoyChecksList />,
        withMenu: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
      },
      {
        path: routesConfig.inventory.eoyChecks.detail,
        canGoBack: true,
        element: (
          <EoyCheck
            type={TaskType.eoy_inventory_check}
            origin={PageName.INVENTORY_EOY_CHECK_LIST_PAGE}
          />
        ),
        withMenu: false,
        withAppHeader: false,
      },
    ],
    flag: ALLOWED_IN_EOY_MODE_FLAG,
  },
  {
    path: routesConfig.inventory.eoyRecountChecks.root,
    pageName: PageName.INVENTORY_RECOUNT_CHECK_LIST_PAGE,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.inventory_checks} />,
    children: [
      {
        path: routesConfig.inventory.eoyRecountChecks.root,
        canGoBack: true,
        element: <RecountChecksList />,
        withMenu: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
      },
      {
        path: routesConfig.inventory.eoyRecountChecks.detail,
        canGoBack: true,
        element: (
          <EoyCheck
            type={TaskType.eoy_inventory_recount_check}
            origin={PageName.INVENTORY_RECOUNT_CHECK_LIST_PAGE}
          />
        ),
        withMenu: false,
        withAppHeader: false,
      },
    ],
    flag: ALLOWED_IN_EOY_MODE_FLAG,
  },
  {
    path: routesConfig.inventory.bbdChecks.root,
    flag: BLOCKED_IN_EOY_MODE_FLAG,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.inventory_checks} />,
    children: [
      {
        path: routesConfig.inventory.bbdChecks.root,
        element: <BbdChecksList />,
        canGoBack: true,
        withMenu: false,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        pageName: PageName.INVENTORY_BBD_CHECK_LIST_PAGE,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.bbdChecks.declareExpired,
        canGoBack: true,
        element: <BbdCheckDeclareExpired />,
        withMenu: false,
        withAppHeader: false,
        pageName: PageName.INVENTORY_BBD_DECLARE_EXPIRED,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.bbdChecks.declareClosestBdd,
        canGoBack: true,
        element: <BbdCheckDeclareClosestBdd />,
        withMenu: false,
        withAppHeader: false,
        pageName: PageName.INVENTORY_BBD_DECLARE_CLOSEST_BBD,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
    ],
  },
  {
    path: routesConfig.inventory.freshChecks.root,
    flag: BLOCKED_IN_EOY_MODE_FLAG,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.inventory_checks} />,
    children: [
      {
        path: routesConfig.inventory.freshChecks.root,
        element: <FreshChecksList />,
        canGoBack: true,
        withMenu: false,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        pageName: PageName.INVENTORY_FRESH_CHECK_LIST_PAGE,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.freshChecks.quantity,
        canGoBack: true,
        element: <FreshCheckTask />,
        withMenu: false,
        withAppHeader: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
    ],
  },
  {
    path: routesConfig.inventory.tooGoodToGo.root,
    flag: BLOCKED_IN_EOY_MODE_FLAG,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.rescue_bag} />,
    children: [
      {
        path: routesConfig.inventory.tooGoodToGo.root,
        canGoBack: true,
        withTabBar: true,
        element: <TGTGBagSelectionPage />,
        withMenu: true,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.tooGoodToGo.prepareBag,
        canGoBack: true,
        element: <TGTGPrepareBagPage />,
        withMenu: false,
        withAppHeader: false,
        pageName: PageName.BAG_PREPARATION,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.tooGoodToGo.confirmation,
        canGoBack: true,
        element: <TGTGBagConfirmationPage />,
        withMenu: false,
        withAppHeader: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.tooGoodToGo.selectProduct,
        canGoBack: true,
        element: <TGTGSelectProductPage />,
        withMenu: false,
        withAppHeader: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.tooGoodToGo.noEANProducts,
        canGoBack: true,
        element: <TGTGSelectNoEANProductPage />,
        withAppHeader: false,
        withTabBar: false,
        flag: BLOCKED_IN_EOY_MODE_FLAG,
      },
    ],
  },
  {
    path: routesConfig.inventory.inboundDeviationChecks.root,
    pageName: PageName.INVENTORY_INBOUND_DEVIATION_CHECK_LIST_PAGE,
    flag: BLOCKED_CORRECTIONS_IN_EOY_MODE_FLAG,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.inventory_checks} />,
    children: [
      {
        path: routesConfig.inventory.inboundDeviationChecks.root,
        element: <InboundDeviationCheckListPage />,
        canGoBack: true,
        withMenu: false,
        withTabBar: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        flag: BLOCKED_CORRECTIONS_IN_EOY_MODE_FLAG,
      },
      {
        path: routesConfig.inventory.inboundDeviationChecks.quantity,
        canGoBack: true,
        element: <InboundDeviationCheckQuantityPage />,
        withMenu: false,
        withAppHeader: false,
        flag: BLOCKED_CORRECTIONS_IN_EOY_MODE_FLAG,
      },
    ],
  },
  {
    path: routesConfig.inventory.shelfChanges.root,
    pageName: PageName.INVENTORY_SHELF_CHANGES_PAGE,
    flag: ENABLE_SHELF_CHANGE_ASSISTANT_FLAG,
    children: [
      {
        path: routesConfig.inventory.shelfChanges.root,
        canGoBack: true,
        element: <ShelfChangesPage />,
        withMenu: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
        flag: ENABLE_SHELF_CHANGE_ASSISTANT_FLAG,
      },
      {
        path: routesConfig.inventory.shelfChanges.detail,
        canGoBack: true,
        element: <ShelfChangeDetailsPage />,
        withMenu: false,
        withAppHeader: false,
        flag: ENABLE_SHELF_CHANGE_ASSISTANT_FLAG,
      },
    ],
  },
  {
    path: routesConfig.inventory.batteryManagement.root,
    pageName: PageName.INVENTORY_BATTERY_MANAGEMENT_PAGE,
    children: [
      {
        path: routesConfig.inventory.batteryManagement.root,
        canGoBack: true,
        element: <BatteryManagementListPage />,
        withMenu: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
      },
      {
        path: routesConfig.inventory.batteryManagement.create,
        canGoBack: true,
        element: <BatteryCreatePage />,
        withMenu: false,
        withAppHeader: false,
      },
      {
        path: routesConfig.inventory.batteryManagement.report,
        canGoBack: true,
        element: <BatteryReportPage />,
        withMenu: false,
        withAppHeader: false,
      },
    ],
  },
  {
    path: routesConfig.inventory.batteryCount.root,
    pageName: PageName.INVENTORY_BATTERY_COUNT_PAGE,
    children: [
      {
        path: routesConfig.inventory.batteryCount.root,
        canGoBack: true,
        element: <BatteryCountListPage />,
        withMenu: false,
        withAppHeader: false,
        withOngoingActivityBanner: true,
      },
      {
        path: routesConfig.inventory.batteryCount.scan,
        canGoBack: true,
        element: <BatteryCountScanPage />,
        withMenu: false,
        withAppHeader: false,
      },
      {
        path: routesConfig.inventory.batteryCount.create,
        canGoBack: true,
        element: <BatteryCreatePage />,
        withMenu: false,
        withAppHeader: false,
      },
      {
        path: routesConfig.inventory.batteryCount.missing,
        canGoBack: true,
        element: <BatteryCountMissingPage />,
        withMenu: false,
        withAppHeader: false,
      },
    ],
  },
  {
    path: routesConfig.activities.deliveryCheckIn.root,
    pageName: PageName.DELIVERY_CHECK_IN,
    noLayout: true,
    element: <ActivityRouteTracker activityType={ActivityType.delivery_check_in} />,
    children: [
      {
        path: routesConfig.activities.deliveryCheckIn.root,
        canGoBack: true,
        element: <DeliveryCheckIn />,
        withAppHeader: false,
        withWaitingOrdersBannerOfType: "none",
      },
      {
        path: routesConfig.activities.deliveryCheckIn.transportEquipmentConfirm,
        canGoBack: true,
        element: <TransportEquipmentConfirmationPage />,
        withAppHeader: false,
        pageName: PageName.TRANSPORT_EQUIPMENT_CONFIRMATION,
        flag: ENABLE_TRANSPORT_EQUIPMENT_FLOW_FLAG,
      },
      {
        path: routesConfig.activities.deliveryCheckIn.transportEquipmentCount,
        canGoBack: true,
        element: <TransportEquipmentCountPage />,
        withAppHeader: false,
        pageName: PageName.TRANSPORT_EQUIPMENT_COUNT,
        flag: ENABLE_TRANSPORT_EQUIPMENT_FLOW_FLAG,
      },
    ],
  },
  {
    path: routesConfig.activities.transportEquipment.precount,
    canGoBack: true,
    element: <TransportEquipmentPrecountPage />,
    withAppHeader: false,
    pageName: PageName.TRANSPORT_EQUIPMENT_PRECOUNT,
    flag: ENABLE_TRANSPORT_EQUIPMENT_FLOW_FLAG,
  },
  {
    path: routesConfig.activities.timedActivities.list,
    canGoBack: true,
    element: <TimedActivitiesListPage />,
    withTabBar: true,
    highlightedTab: "activities",
    withOngoingActivityBanner: true,
    pageName: PageName.HUB_ACTIVITIES,
  },
  {
    path: routesConfig.activities.timedActivities.details,
    canGoBack: true,
    element: <TimedActivitiesDetails />,
    withTabBar: true,
    highlightedTab: "activities",
    withAppHeader: false,
  },
  {
    path: "*",
    canGoBack: false,
    element: <Navigate to={routesConfig.activities.root} />,
    withTabBar: true,
    highlightedTab: "activities",
  },
]);

export function AppRouter() {
  const { isAuthenticated, isLoading } = useAuth0();
  const { isLoadingAppPWAState, displayMode } = useAppPWAStateStore(
    useShallow((state) => ({
      isLoadingAppPWAState: state.isLoading,
      displayMode: state.displayMode,
    })),
  );
  const isIdentified = useEmployeeStore((state) => state.isIdentified);
  const router = useMemo(() => {
    let usedRoutes: RouteObject[];
    if (config.environment.FORCE_PWA && (isLoadingAppPWAState || displayMode === "browser")) {
      usedRoutes = isInBrowserRoutes;
    } else if (isLoading || !isAuthenticated || !isIdentified) {
      usedRoutes = publicRoutes;
    } else {
      usedRoutes = protectedRoutes;
    }
    return createBrowserRouter([
      {
        element: <AppLayout />,
        children: usedRoutes,
      },
    ]);
  }, [displayMode, isAuthenticated, isIdentified, isLoading, isLoadingAppPWAState]);

  return <RouterProvider router={router} />;
}
